import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// import { toUtcStart, toUtcDeadline, toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import {
    listDraftItems,
    createItem,
    updateItem,
    // reorderSiblings,
    apiPublish,
    getAllListVersions,
    // apiGetCurrentList,
    // apiActivateVersion,
    apiGetListByVersion,
} from '../api';
import type { DraftItem, KeyId, ParentKey, OrderMap } from '../types.local';
import type { WbsListMeta } from '../api';
import { keyOf } from '../types.local';
import { between } from '../lib/order';
import { validateWbsWeights } from '../validators/wbsWeightValidator';
import { toNumberOrNull } from 'src/utils/formatter';
import { useToast } from 'src/shared/ui/toast/useToast';

/* =========================
   유틸: 정렬 스냅샷 생성/비교 (KeyId 기준)
   ========================= */
function buildOrderMap(
    items: Array<{ nodeId?: string | null; cid?: string; parentNodeId: string | null; orderIndex: number | null }>,
): OrderMap {
    const groups = new Map<ParentKey, { keyId: KeyId; orderIndex: number }[]>();
    for (const it of items) {
        const parent = it.parentNodeId ?? null;
        if (!groups.has(parent)) groups.set(parent, []);
        groups.get(parent)!.push({ keyId: keyOf(it), orderIndex: it.orderIndex ?? 0 });
    }
    const map = new Map<ParentKey, KeyId[]>();
    for (const [parent, arr] of groups) {
        arr.sort((a, b) => a.orderIndex - b.orderIndex);
        map.set(
            parent,
            arr.map((x) => x.keyId),
        );
    }
    return map;
}

function mapsEqual(a: OrderMap, b: OrderMap) {
    if (a.size !== b.size) return false;
    for (const [k, A] of a) {
        const B = b.get(k);
        if (!B || A.length !== B.length) return false;
        for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
    }
    return true;
}

type DiffKind = 'ADDED' | 'MODIFIED' | 'DELETED' | null;

const DIFF_KEYS: Array<keyof DraftItem> = [
    'name',
    'plannedStart',
    'plannedFinish',
    'weight',
    'memo',
    'parentNodeId',
    'orderIndex',
];

const eq = (a: any, b: any) => {
    if (a === b) return true;
    if (a == null && b == null) return true;
    return String(a ?? '') === String(b ?? '');
};

const buildChangeKindMap = (baseline: DraftItem[], drafts: DraftItem[]): Record<KeyId, DiffKind> => {
    const out: Record<KeyId, DiffKind> = {};

    const baseByNode = new Map<string, DraftItem>();
    for (const b of baseline) {
        if (b.nodeId) baseByNode.set(b.nodeId, b);
    }

    const draftByNode = new Map<string, DraftItem>();
    for (const d of drafts ?? []) {
        const id = d.nodeId ?? (d as any).cid;
        if (!id) continue;

        if (draftByNode.has(id) && process.env.NODE_ENV !== 'production') {
            console.error('[WBS] Duplicate draft entry detected in change-map (keyId):', id, {
                prev: draftByNode.get(id),
                next: d,
            });
        }

        draftByNode.set(id, d); // 기존 동작 유지(마지막 것 우선) + 중복 감지
    }

    // 1) 드래프트 기준으로 상태 판정
    for (const [id, d] of draftByNode) {
        const base = d.nodeId ? baseByNode.get(d.nodeId) : undefined;
        const key = d.nodeId ?? (d as any).cid!;

        // 소프트 삭제 (isDeleted 기반 드래프트가 들어온 경우)
        if ((d as any).isDeleted) {
            out[key] = 'DELETED';
            continue;
        }

        // nodeId가 없거나, nodeId는 있지만 baseline에 없으면 → 신규
        if (!base) {
            out[key] = 'ADDED';
            continue;
        }

        // baseline과 내용 비교 → 수정 여부
        let diff = false;
        for (const k of DIFF_KEYS) {
            if (!eq((d as any)?.[k], (base as any)?.[k])) {
                diff = true;
                break;
            }
        }
        out[key] = diff ? 'MODIFIED' : null;
    }

    // 2) 드래프트가 없는 순수 baseline 노드들은 null(변경 없음)
    for (const b of baseline) {
        const key = keyOf(b);
        if (out[key] === undefined) {
            out[key] = null;
        }
    }

    return out;
};

/* =========================
   훅: useWbsDraft
   - 단건 생성/수정
   - 형제 정렬 저장
   - 퍼블리시
   - 서브트리 삭제(로컬)
   ========================= */
export function useWbsDraft(projectId: string) {
    const toast = useToast();

    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    // 서버 스냅샷
    const [serverItems, setServerItems] = useState<DraftItem[]>([]);
    const [loading, setLoading] = useState(false);

    const [versions, setVersions] = useState<WbsListMeta[]>([]);
    const [currentVersion, setCurrentVersion] = useState<number | null>(null);
    const [baseline, setBaseline] = useState<DraftItem[]>([]); // 발행본을 DraftItem 형태로 변환 저장

    function toDraftLike(it: any, publishedVersion?: number | null): DraftItem {
        return {
            nodeId: it.nodeId,
            parentNodeId: it.parentNodeId,
            orderIndex: it.orderIndex ?? 0,
            name: it.name ?? '',
            plannedStart: it.plannedStart ?? null,
            plannedFinish: it.plannedFinish ?? null,
            durationDays: it.durationDays ?? null,
            weight: it.weight ?? null,
            memo: it.memo ?? null,
            level: it.level ?? undefined,
            code: it.code ?? undefined,
            publishedListVersion: publishedVersion ?? it.publishedListVersion ?? null,
            isDeleted: it.isDeleted ?? false,
        };
    }

    // 로컬 변경(내용 · 정렬)
    const [local, setLocal] = useState<Record<KeyId, Partial<DraftItem>>>({});
    const [dirtyContent, setDirtyContent] = useState<Set<KeyId>>(new Set());
    const [baselineOrder, setBaselineOrder] = useState<OrderMap>(new Map());

    // 선택
    const [selectedKey, setSelectedKey] = useState<KeyId | null>(null);

    // ref: 최신 items 접근용
    const itemsRef = useRef<DraftItem[] | null>(null);

    // [수정] 버전/베이스라인/드래프트 동시 로드 -> 머지
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            // 1) 버전 목록 + 현재 활성화된 리스트 조회
            const verList = await getAllListVersions(projectId);
            setVersions(verList ?? []);

            const latestVer = verList && verList.length ? Math.max(...verList.map((v) => v.version)) : null;
            setCurrentVersion(latestVer);

            const baseSource =
                latestVer != null ? await apiGetListByVersion(projectId, latestVer) : { list: null, items: [] };

            // 2) 베이스라인: 현재 활성화된 발행본 → DraftItem 형태로 변환. 엔드포인트가 서로 달라 서로 1:1로 비교해야 하는데 미세하게 타입등이 다를 수 있기 때문에 normalize한다.
            const baseItems = (baseSource?.items ?? []).map((x: any) => toDraftLike(x, latestVer));
            setBaseline(baseItems);

            // 3) 드래프트 로드
            const drafts = await listDraftItems(projectId);
            // listDraftItems: draft상태(publishedListVersion==null)인 wbsItemVersion[] 리턴

            const effectiveDrafts = (drafts ?? []).filter((draft: any) => {
                const pv = draft.publishedListVersion ?? null;

                if (latestVer == null) {
                    return pv == null;
                }

                return pv == null || pv === latestVer;
            });

            setServerItems(effectiveDrafts);

            // 4) 정렬 스냅샷:
            // 드래프트가 있으면 그걸 기준,
            // 없으면 베이스라인 기준으로 찍는다.
            const orderSource = effectiveDrafts.length > 0 ? effectiveDrafts : baseItems;

            setBaselineOrder(
                buildOrderMap(
                    orderSource.map((v: any) => ({
                        nodeId: v.nodeId ?? null,
                        cid: (v as any).cid,
                        parentNodeId: v.parentNodeId ?? null,
                        orderIndex: v.orderIndex ?? 0,
                    })),
                ),
            );

            // 5) 최초 선택
            if (!selectedKey) {
                const first = effectiveDrafts[0] ?? baseItems[0] ?? null;
                if (first) setSelectedKey(keyOf(first));
            }

            // console.log('drafts : ', drafts);
            // console.log('ordreSource: ', orderSource);
        } finally {
            setLoading(false);
        }
    }, [projectId, selectedKey]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    // [다시 수정] 화면에 순서 정렬해도 정렬된 내용이 안보임 수정
    // 변경 1단계: baseItems
    const baseItems: DraftItem[] = useMemo(() => {
        const byNode = new Map<string, DraftItem>();
        for (const b of baseline) {
            if (b.nodeId) {
                byNode.set(b.nodeId, b);
            }
        }
        for (const d of serverItems ?? []) {
            if (d.nodeId) {
                if ((d as any).isDeleted) {
                    byNode.delete(d.nodeId);
                } else {
                    byNode.set(d.nodeId, { ...byNode.get(d.nodeId), ...d });
                }
            } else {
                const key = d.cid ?? `__new_${Math.random()}`;
                byNode.set(key, d);
            }
        }
        return Array.from(byNode.values());
    }, [baseline, serverItems]);

    // 변경 2단계: items = baseItems + local 패치 적용
    const items: DraftItem[] = useMemo(() => {
        const merged = baseItems.map((it) => {
            const k = keyOf(it);
            return { ...it, ...(local[k] ?? {}) };
        });
        itemsRef.current = merged;
        return merged;
    }, [baseItems, local]);

    /* ---------- 정렬 dirty ---------- */
    const currentOrder = useMemo(
        () =>
            buildOrderMap(
                items.map((v) => ({
                    nodeId: v.nodeId ?? null,
                    cid: (v as any).cid,
                    parentNodeId: v.parentNodeId ?? null,
                    orderIndex: v.orderIndex ?? 0,
                })),
            ),
        [items],
    );
    const reorderDirty = useMemo(() => !mapsEqual(currentOrder, baselineOrder), [currentOrder, baselineOrder]);

    /* ---------- 내용 dirty ---------- */
    const dirty = dirtyContent.size > 0 || reorderDirty;

    /* ---------- 내용 수정(로컬 패치) ---------- */
    const patchLocal = useCallback((nodeIdOrCid: KeyId, patch: Partial<DraftItem>) => {
        setLocal((prev) => ({ ...prev, [nodeIdOrCid]: { ...(prev[nodeIdOrCid] ?? {}), ...patch } }));
        setDirtyContent((prev) => {
            const next = new Set(prev);
            next.add(nodeIdOrCid);
            return next;
        });
    }, []);

    const inlineEditName = useCallback(
        async (nodeId: string, name: string) => {
            await updateItem(projectId, nodeId, { name });
        },
        [projectId],
    );

    const createRoot = useCallback(async () => {
        // 루트는 반드시 null로 보냄 (undefined 금지)
        await createItem(projectId, { parentNodeId: null, name: 'New WBS Item' });
        await refresh();
    }, [projectId, refresh]);

    /* ---------- [추가] baseline node 집합 노출 ---------- */
    const baselineNodeIds = useMemo(() => {
        const s = new Set<string>();
        for (const it of baseline) {
            if (it.nodeId) {
                s.add(it.nodeId);
            }
        }
        return s;
    }, [baseline]);

    const addSameLevel = useCallback(
        async (mode: 'append' | 'reassign' = 'append'): Promise<KeyId | null> => {
            if (!selectedKey) return null;
            const sel = items.find((x) => keyOf(x) === selectedKey);
            if (!sel) return null;
            const parentNodeId = sel?.parentNodeId ?? null;
            const parent = items.find((x) => keyOf(x) === parentNodeId);

            // 형제 목록
            const siblings = items
                .filter((x) => (x.parentNodeId ?? null) === parentNodeId && !x.isDeleted)
                .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

            let orderIndex: number | undefined = undefined;

            if (mode === 'reassign') {
                const idx = siblings.findIndex((x) => keyOf(x) === selectedKey);
                const prev = siblings[idx]?.orderIndex ?? null; // 선택 바로 뒤로 들어가도록
                const next = siblings[idx + 1]?.orderIndex ?? null;
                orderIndex = between(prev, next);
            } else {
                // append: 맨 뒤
                const last = siblings[siblings.length - 1];
                orderIndex = last?.orderIndex != null ? last.orderIndex + 10 : 10;
            }

            const created = await createItem(projectId, {
                parentNodeId,
                name: 'New WBS Item',
                plannedStart: null,
                // plannedStart: parent?.plannedStart ? toProjectDateOnly(parent.plannedStart, zone) : null,
                plannedFinish: null,
                // plannedFinish: parent?.plannedFinish ? toProjectDateOnly(parent.plannedFinish, zone) : null,
                orderIndex,
            });
            await refresh();

            if (created?.nodeId) return created.nodeId as KeyId;
            if ((created as any)?.cid) return (created as any).cid as KeyId;
            return null;
        },
        [items, projectId, refresh, selectedKey],
    );

    // [변경] 하위 추가
    const addChild = useCallback(
        async (mode: 'append' | 'reassign' = 'append'): Promise<KeyId | null> => {
            if (!selectedKey) return null;
            const sel = items.find((x) => keyOf(x) === selectedKey);
            const parentNodeId = sel?.nodeId ?? null;
            if (!parentNodeId) {
                // alert('하위 추가는 서버에 저장된 항목에서만 가능합니다. 먼저 저장해 주세요.');
                toast.warning('하위 추가는 서버에 저장된 항목에서만 가능합니다. 먼저 저장해 주세요', {
                    title: '추가 불가',
                });

                return null;
            }

            const children = items
                .filter((x) => (x.parentNodeId ?? null) === parentNodeId && !x.isDeleted)
                .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

            let orderIndex: number;

            if (mode === 'reassign') {
                const first = children[0]?.orderIndex ?? null;
                orderIndex = between(null, first);
            } else {
                const last = children[children.length - 1];
                orderIndex = last?.orderIndex != null ? last.orderIndex + 10 : 10;
            }

            const created = await createItem(projectId, {
                parentNodeId,
                name: 'New WBS Item',
                plannedStart: null,
                plannedFinish: null,
                orderIndex,
            });
            await refresh();

            if (created?.nodeId) return created.nodeId as KeyId;
            if ((created as any)?.cid) return (created as any).cid as KeyId;
            return null;
        },
        [items, projectId, refresh, selectedKey, toast],
    );

    // Swap 방식
    const moveSibling = useCallback(
        async (key: KeyId, dir: 'up' | 'down') => {
            const list = itemsRef.current ?? items;
            const me = list.find((x) => keyOf(x) === key);
            if (!me || !me.nodeId) return;

            const parentNodeId = me.parentNodeId ?? null;

            const siblings = list
                .filter((x) => (x.parentNodeId ?? null) === parentNodeId && !x.isDeleted)
                // ✅ orderIndex가 같거나 null일 때도 순서가 흔들리지 않도록 tie-break 추가
                .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0) || keyOf(a).localeCompare(keyOf(b)));

            const idx = siblings.findIndex((x) => keyOf(x) === key);
            const target = dir === 'up' ? idx - 1 : idx + 1;
            if (idx < 0 || target < 0 || target >= siblings.length) return;

            const targetItem = siblings[target];
            if (!targetItem?.nodeId) return;

            // ✅ swap 방식: 두 항목의 orderIndex 교환 (null 방어: 위치 기반 fallback 정수)
            const aKey = keyOf(me);
            const bKey = keyOf(targetItem);

            const aOrder = me.orderIndex ?? (idx + 1) * 10;
            const bOrder = targetItem.orderIndex ?? (target + 1) * 10;

            // 1) 로컬 업데이트(둘 다)
            setLocal((prevState) => ({
                ...prevState,
                [aKey]: { ...(prevState[aKey] ?? {}), orderIndex: bOrder },
                [bKey]: { ...(prevState[bKey] ?? {}), orderIndex: aOrder },
            }));

            // 2) 서버 업데이트(둘 다)
            await updateItem(projectId, me.nodeId, { parentNodeId, orderIndex: bOrder });
            await updateItem(projectId, targetItem.nodeId, { parentNodeId, orderIndex: aOrder });

            // 3) 서버 스냅샷 동기화(권장)
            await refresh();
        },
        [items, projectId, refresh, setLocal],
    );

    // [추가] 미리보기 코드 계산기
    const buildPreviewCodes = useCallback((items: DraftItem[]): Record<KeyId, string> => {
        const byParent = new Map<string | null, DraftItem[]>();
        for (const it of items) {
            if (it.isDeleted) continue;
            const p = it.parentNodeId ?? null;
            if (!byParent.has(p)) byParent.set(p, []);
            byParent.get(p)!.push(it);
        }
        for (const arr of byParent.values()) {
            arr.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0) || keyOf(a).localeCompare(keyOf(b)));
        }

        const map: Record<KeyId, string> = {};
        const walk = (parentId: string | null, prefix: string | null) => {
            const kids = byParent.get(parentId) ?? [];
            kids.forEach((k, i) => {
                const code = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
                map[keyOf(k)] = code;
                // nodeId가 있든 없든, 화면 계층은 parent/child로 이어지므로 nodeId 기준으로 탐색
                // 단, 아직 저장 안된 신규 노드는 nodeId가 없을 수 있으니 keyOf로 안전 처리
                // 여기서는 parentId에 nodeId를 기대하므로, 저장 전 신규 자식까지 재귀하지 않는다.
                // (저장 전 신규 자식 재귀가 필요하면 parent 키 체계 확장 필요)
                if (k.nodeId) walk(k.nodeId, code);
            });
        };
        walk(null, null);
        return map;
    }, []);

    const previewCodeMap = useMemo(() => buildPreviewCodes(items), [buildPreviewCodes, items]);

    /* ---------- 내용 저장(단건 저장. dirty 경로 사용하지 않음. dirty경로 사용하면 두번 '저장'해야 데이터베이스에 저장되는 문제가 현재 있음) ---------- */
    const saveSingleItem = useCallback(
        async (nodeId: string, payload: Partial<DraftItem>) => {
            // 날짜 -> RFC3339로 변환
            const body: any = {};
            if (payload.name !== undefined) body.name = payload.name;
            // if ('plannedStart' in payload) body.plannedStart = dateOnlyToUtcIso(payload.plannedStart);
            if ('plannedStart' in payload)
                // body.plannedStart = payload.plannedStart ? toUtcStart(payload.plannedStart, zone) : null;
                body.plannedStart = payload.plannedStart ?? null;
            // if ('plannedFinish' in payload) body.plannedFinish = dateOnlyToUtcIso(payload.plannedFinish);
            if ('plannedFinish' in payload)
                // body.plannedFinish = payload.plannedFinish ? toUtcDeadline(payload.plannedFinish, zone) : null;
                body.plannedFinish = payload.plannedFinish ?? null;
            if ('weight' in payload) body.weight = toNumberOrNull(payload.weight ?? null);
            if ('memo' in payload) body.memo = payload.memo ?? null;
            if ('isDeleted' in payload) body.isDeleted = payload.isDeleted;

            await updateItem(projectId, nodeId, body);
            await refresh();
        },
        [projectId, refresh],
    );

    /* ---------- 내용 저장(내용 dirty만 PATCH) ---------- */
    const saveContent = useCallback(async () => {
        if (dirtyContent.size === 0) return;

        const updates: Array<Promise<any>> = [];
        for (const k of Array.from(dirtyContent)) {
            // 서버 항목만 PATCH
            const base = items.find((x) => keyOf(x) === k);
            if (!base?.nodeId) continue;

            const patch = local[k] ?? {};
            const payload: any = {};
            if (patch.name !== undefined) payload.name = patch.name;
            // if ('plannedStart' in patch) payload.plannedStart = dateOnlyToUtcIso(patch.plannedStart);
            if ('plannedStart' in patch)
                // payload.plannedStart = patch.plannedStart ? toUtcStart(patch.plannedStart, zone) : null;
                payload.plannedStart = patch.plannedStart ?? null;
            // if ('plannedFinish' in patch) payload.plannedFinish = dateOnlyToUtcIso(patch.plannedFinish);
            if ('plannedFinish' in patch)
                // payload.plannedFinish = patch.plannedFinish ? toUtcDeadline(patch.plannedFinish, zone) : null;
                payload.plannedFinish = patch.plannedFinish ?? null;
            if ('weight' in patch) payload.weight = toNumberOrNull(patch.weight ?? null);
            if ('memo' in patch) payload.memo = patch.memo ?? null;
            if (Object.keys(payload).length === 0) continue;

            updates.push(updateItem(projectId, base.nodeId!, payload));
        }
        if (updates.length) await Promise.all(updates);

        // 로컬 내용 dirty 초기화
        setLocal((prev) => {
            const next = { ...prev };
            for (const id of Array.from(dirtyContent)) delete next[id];
            return next;
        });
        setDirtyContent(new Set());
        await refresh();
    }, [dirtyContent, items, local, projectId, refresh]);

    const publish = useCallback(async (): Promise<number | null> => {
        // 빈 WBS면 서버로 요청 자체를 보내지 않음
        if ((itemsRef.current?.length ?? 0) === 0) {
            // alert('아직 WBS가 없습니다. 먼저 항목을 추가하세요.');
            toast.warning('아직 WBS가 없습니다. 먼저 항목을 추가하세요.', {
                title: '게시 불가',
            });

            return null;
        }
        // 내용 수정이 로컬에만 남아있다면 먼저 저장 여부 확인
        if (dirtyContent.size > 0) {
            const ok = window.confirm('내용 수정 사항이 저장되지 않았습니다. 먼저 "저장" 하시겠습니까?');
            if (ok) {
                await saveContent();
            }
        }

        // Weight 검증
        const raw = itemsRef.current ?? [];
        const nodes = raw
            .filter((it) => !!it.nodeId)
            .map((it) => ({
                nodeId: it.nodeId as string,
                parentNodeId: (it.parentNodeId ?? null) as string | null,
                weight: it.weight ?? null,
                isDeleted: it.isDeleted ?? false,
            }));

        // 위에 복잡하게 한 것은 타입경고를 지우기 위한 것임
        const { ok, mismatches } = validateWbsWeights(nodes);
        if (!ok) {
            // mismatchs를 alet/모달로 출력
            const top = mismatches
                .slice(0, 5)
                .map(
                    (m: any) =>
                        `- ${m.parentName}: parent = ${m.parentWeight ?? 'null'}, childrenSum = ${m.childSum ?? 'null'}`,
                )
                .join('\n');

            toast.error(
                `가중치 합계가 맞지 않아 Publish(게시)할 수 없습니다.\n\n` +
                    `${top}\n` +
                    (mismatches.length > 5 ? `\n...외 ${mismatches.length - 5}건` : ''),
                {
                    title: '가중치 검증 실패',
                    duration: 5000,
                },
            );

            return null;
        }

        // eslint-disable-next-line no-restricted-globals
        if (!confirm('현재 상태로 WBS List를 발행하시겠습니까? 발행후에는 WBS 순서 변경등의 제약이 있을 수 있습니다.'))
            return null;

        // const { version } = await apiPublish(projectId);
        // alert(`WBS v${version}이(가) 퍼블리시되었습니다.`);
        // await refresh();

        try {
            const { version } = await apiPublish(projectId);

            // alert(`WBS v${version}이(가) 게시(퍼블리시)되었습니다.`);
            toast.success(`WBS v${version}이(가) 게시되었습니다.`, {
                title: '게시 완료',
            });

            await refresh();
            // 성공하면 version 반환
            return version;
        } catch (err: any) {
            const status = err?.response?.status;
            const code = err?.response?.data?.error?.code;

            // 서버가 400 + WBS_EMPYT로 내려주는 케이스 대응
            if (status === 400 && code === 'WBS_EMPTY') {
                toast.warning('아직 WBS가 없습니다. 먼저 항목을 추가하세요.', {
                    title: '게시 불가',
                });

                return null;
            }

            if (status === 400 && code === 'WBS_WEIGHT_MISMATCH') {
                const details = err?.response?.data?.error?.details;
                const list = details?.mismatches ?? [];
                const top = list
                    .slice(0, 5)
                    .map(
                        (m: any) =>
                            `- ${m.parentName}: parent = ${m.parentWeight ?? 'null'}, childrenSum = ${m.childSum ?? 'null'}`,
                    )
                    .join('\n');

                toast.error(
                    `가중치 합계가 맞지 않아 Publish(게시)할 수 없습니다.\n\n` +
                        `${top}\n` +
                        (list.length > 5 ? `\n...외 ${list.length - 5}건` : ''),
                    {
                        title: '가중치 검증 실패',
                        duration: 5000,
                    },
                );
                return null;
            }

            // alert('WBS 게시(퍼블리시)중 에러가 발생했습니다.');
            toast.error('WBS 게시(퍼블리시)중 에러가 발생했습니다.', {
                title: '게시 실패',
            });

            return null;
        }
    }, [dirtyContent.size, projectId, refresh, saveContent, toast]);

    /* ---------- 서브트리 삭제(로컬) ---------- */
    const removeLocal = useCallback((baseKey: KeyId) => {
        const byKey = new Map<KeyId, DraftItem>();
        (itemsRef.current ?? []).forEach((it) => byKey.set(keyOf(it), it));

        const toDelete = new Set<KeyId>();
        const dfs = (k: KeyId) => {
            if (toDelete.has(k)) return;
            toDelete.add(k);
            for (const it of itemsRef.current ?? []) {
                const pKey = (it as any).parentKey ?? null; // parentKey를 쓰는 경우
                const parent = (pKey ?? it.parentNodeId ?? null) as KeyId | null; // 둘 중 있는 값 사용
                if (parent === k) dfs(keyOf(it));
            }
        };
        dfs(baseKey);

        // 로컬 스테이트에서 제거
        setLocal((prev) => {
            const next = { ...prev };
            for (const k of toDelete) delete next[k];
            return next;
        });
        // 서버 아이템 목록 자체를 제거하려면 아래 라인 사용(순수 로컬 삭제만 원하면 생략)
        // setServerItems(prev => (prev ?? []).filter(it => !toDelete.has(keyOf(it))));
    }, []);

    // const changeKindMap = useMemo(() => buildChangeKindMap(items), [items]);
    /**
     * 퍼블리시 이력없는 프로젝트
     *   베이스라인 없음. draft만 있음 -> 전부 ADDED
     * 퍼블리시 후
     *   기존 노드 + 드래프트 없으면 -> null (변경 없음)
     *   기존 노드 + 수정 드래프트 있으면 -> MODIFIED
     *   기존 노드 + 삭제 드래프트(isDeleted) 있으면 -> DELETED
     *   새로 추가된 노드(draft만 있음) -> ADDED
     */
    const changeKindMap = useMemo(() => buildChangeKindMap(baseline, serverItems), [baseline, serverItems]);

    // [추가] 소프트 삭제/삭제 취소 (saveSingleItem 재사용)
    const softDelete = useCallback(
        async (nodeId: string) => {
            await saveSingleItem(nodeId, { isDeleted: true });
            await refresh();
        },
        [saveSingleItem, refresh],
    );

    const undoDelete = useCallback(
        async (nodeId: string) => {
            await saveSingleItem(nodeId, { isDeleted: false });
            await refresh();
        },
        [saveSingleItem, refresh],
    );

    /* ---------- 외부로 제공 ---------- */
    return {
        // 상태
        loading,
        items,
        selectedKey,
        dirty,
        reorderDirty,
        dirtyContent,

        createRoot,

        inlineEditName,

        // 선택/편집
        setSelectedKey,
        patchLocal,

        // 구조 변경
        baselineNodeIds,
        addSameLevel,
        addChild,
        moveSibling,
        removeLocal,

        // 저장/퍼블리시
        // saveReorder,
        saveSingleItem,
        saveContent,
        publish,

        // 재조회
        refresh,

        versions,
        currentVersion,

        hasDraft: serverItems.length > 0,
        draftCount: serverItems.length,
        latestPublishedVersion: versions.length ? Math.max(...versions.map((v) => v.version)) : null,

        previewCodeMap,
        changeKindMap,
        softDelete,
        undoDelete,
    };
}

export type UseWbsDraftReturn = ReturnType<typeof useWbsDraft>;
